import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import useCsrService from '../../services/csr-api-redux';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchLawEnforcementCollisionById,
  publishLECollision,
  submitLECollisionToEcollision,
  updateLECollision,
  updateLECollisionStatus,
  uploadCanvasImage,
} from '../../state/slices/lawEnforcementCollisionsSlice';
import { AppDispatch, RootState } from '../../state/store';
import {
  Collision,
  DamageSelection,
  Driver,
  Insurance,
  Passenger,
  Vehicle,
} from '../../models/collision';
import {
  GoAAccordion,
  GoABadge,
  GoAButton,
  GoAButtonGroup,
  GoACallout,
  GoAContainer,
  GoADivider,
  GoAFormItem,
  GoAFormItemRequirement,
  GoAIconType,
  GoAInput,
  GoAInputText,
  GoAInputType,
  GoAModal,
  GoARadioGroup,
  GoARadioItem,
  GoASpacer,
  GoATextArea,
} from '@abgov/react-components';
import { getReportStatus, getViolations } from './law-enforcement-common';
import {
  displaySection,
  getYesNoOrUnknownString,
} from '../../common/ui-library';
import { toInteger } from 'lodash';
import moment from 'moment';
import {
  CollisionTypes,
  fetchConfiguration,
  getConfigurationByDescription,
  isHitAndRun,
  LightCondition,
  ObjectIdentification,
} from '../../state/slices/configurationSlice';
import { ReviewMapComponent } from '../location/location-review-component';
import { REACT_APP_GOOGLE_API_KEY } from '../../state/configuration';
import styles from './law-enforcement.module.css';
import {
  CollisionMapComponent,
  DEFAULT_LOCATION,
} from '../location/map-component';
import {
  fetchMediaListByCollisionId,
  fetchMediaZipByCollisionId,
} from '../../state/slices/mediaSlice';
import { MediaCopyGenerater } from '../review/review-copy-generators';
import {
  DamageSketch,
  DesktopDamageSelectorLayoutConfiguration,
  MobileDamageSelectorLayoutConfiguration,
} from '../sketches/damage';
import { useIsDesktop } from '../../utils/isDesktop';
import p5 from 'p5';
import { InitialPointOfImpact } from '../../models/initial-point-of-impact';
import { Person } from '../../models/person';
import {
  pushNotification,
  removeAllNotifications,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';
import { getAllLEOfficers } from '../../state/slices/lawEnforcementOfficersSlice';
import {
  isAdmin,
  isLawEnforcementAdmin,
  isLawEnforcementOfficer,
  loadCsrUser,
} from '../../state/slices/authSlice';
import {
  hitAndRunFlowQuestions,
  regularFlowQuestions,
} from '../eligibility/eligibility-component';

export interface LawEnforcementCollisionReviewComponentProps {}

export const LawEnforcementCollisionReviewComponent =
  ({}: LawEnforcementCollisionReviewComponentProps) => {
    const apiKey = REACT_APP_GOOGLE_API_KEY;

    const useAppSelector: <TSelected>(
      selector: (state: RootState) => TSelected,
    ) => TSelected = useSelector;

    const stateAuth = useAppSelector((state: RootState) => state.auth);
    const stateConfig = useAppSelector(
      (state: RootState) => state.configuration,
    );
    const lawEnforcementConfig = useAppSelector(
      (state: RootState) => state.lawEnfocementConfiguration,
    );
    const lawEnforcementOfficers = useAppSelector(
      (state: RootState) => state.lawEnforcementLEOfficers,
    );

    const eCollisionSubmittedDateValue = useAppSelector(
      (state: RootState) =>
        state.lawEnforcementCollisions.collision?.ecollisionSubmissionDate,
    );
    const prevECollisionSubmittedDate = useRef(eCollisionSubmittedDateValue);

    const stateMedia = useAppSelector((state: RootState) => state.media);

    const isDesktop = useIsDesktop();
    const { collisionId } = useParams();
    const csrService = useCsrService();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const p5Ref = useRef<HTMLDivElement>(null);
    const containerDiv = useRef<HTMLDivElement>(null);

    const stateLawEnforcementCollisions = useAppSelector(
      (state: RootState) => state.lawEnforcementCollisions,
    );
    const stateConfiguration = useAppSelector(
      (state: RootState) => state.configuration,
    );

    const [damageSketch, setDamageSketch] = useState<DamageSketch>(
      new DamageSketch(),
    );

    const [collision, setCollision] = useState<Collision | undefined>(
      undefined,
    );
    const [isReadOnly, setIsReadOnly] = useState(false);

    const [isSaved, setIsSaved] = useState(true);
    const [triggerSave, setTriggerSave] = useState(false);
    const [navigateToDashboard, setNavigateToDashboard] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [showPendingChangesModal, setShowPendingChangesModal] =
      useState(false);
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [showMapEditModal, setShowMapEditModal] = useState(false);
    const [showMapAccordian, setShowMapAccordian] = useState(false);
    const [showSubmissionSuccessModal, setShowSubmissionSuccessModal] =
      useState(false);
    const [violations, setViolations] = useState<string[]>([]);
    const [fileNumber, setFileNumber] = useState('');
    const [fileNumberText, setFileNumberText] = useState('');
    const [reviewer, setReviewer] = useState('');
    const [eligibilityResponses, setEligibilityResponses] =
      useState<React.ReactNode>(undefined);
    const [collisionType, setCollisionType] = useState('');
    const [collisionSubType, setCollisionSubType] = useState('');
    const [newLatLng, setNewLatLng] = useState<
      { lat: number; lng: number } | undefined
    >(undefined);
    const [latLng, setLatLong] = useState<
      { lat: number; lng: number } | undefined
    >(undefined);
    const [newZoom, setNewZoom] = useState<number | undefined>(undefined);
    const [zoom, setZoom] = useState<number | undefined>(undefined);
    const [newLocationString, setNewLocationString] = useState('');
    const [locationString, setLocationString] = useState('');

    const [reportedDate, setReportedDate] = useState('');
    const [collisionDate, setCollisionDate] = useState('');
    const [collisionTime, setCollisionTime] = useState('');
    const [directionOfTravel, setDirectionOfTravel] = useState('');
    const [
      instersectionsAndRailroadCrosings,
      setIntersectionsAndRailroadCrosings,
    ] = useState('');

    const [occuredInParkingLot, setOccuredInParkingLot] = useState('');
    const [primaryRoadType, setPrimaryRoadType] = useState('');
    const [travelingLane, setTravelingLane] = useState('');
    const [specialFacility, setSpecialFacility] = useState('');
    const [roadSurface, setRoadSurface] = useState('');
    const [roadStraighOrCurved, setRoadStraightOrCurved] = useState('');
    const [damageList, setDamageList] = useState<DamageSelection[]>([]);
    const [additionalDamageDetails, setAdditionalDamageDetails] = useState('');
    const [attachment, setAttachment] = useState('');
    const [carryingLoad, setCarryingLoad] = useState('');
    const [spillItems, setSpillItems] = useState('');

    const [weatherCondition, setWeatherCondition] = useState('');
    const [whichLightCondition, setWhichLightCondition] = useState(''); // light condition a
    const [anyStreetLights, setAnyStreetLights] = useState(''); // light condition b
    const [roadCondition, setRoadCondition] = useState('');
    const [roadSurfaceCondition, setRoadSurfaceCondition] = useState('');
    const [trafficControlDevice, setTrafficControlDevice] = useState('');
    const [trafficControlDeviceState, setTrafficControlDeviceState] =
      useState('');
    const [vehicleCondition, setVehicleCondition] = useState('');
    const [isNoContributingFactors, setIsNoContributingFactors] = useState('');
    const [reporterEmail, setReporterEmail] = useState('');
    const [driver, setDriver] = useState<Driver | undefined>(undefined);
    const [vehicle, setVehicle] = useState<Vehicle | undefined>(undefined);
    const [passengers, setPassengers] = useState<Passenger[]>([]);
    const [isOwner, setIsOwner] = useState(false);
    const [owner, setOwner] = useState<Person | undefined>(undefined);
    const [damageEstimateObtained, setDamageEstimateObtained] = useState(false);
    const [damageEstimate, setDamageEstimate] = useState('');

    const [insuredBy, setInsuredBy] = useState<Insurance | undefined>(
      undefined,
    );
    const [insuredByExpiration, setInsuredByExpiration] = useState('');
    const [additionalDetails, setAdditionalDetails] = useState('');
    const [
      releventPointOfImpactLocationList,
      setReleventPointOfImpactLocationList,
    ] = useState<InitialPointOfImpact[]>([]);

    const [damageSelectionsList, setDamageSelectionsList] =
      useState<DamageSelection[]>();

    const [
      initialPointOfImpactLocationList,
      setInitialPointOfImpactLocationList,
    ] = useState<InitialPointOfImpact[]>([]);

    const [locationAdditionalDetails, setLocationAdditionalDetails] =
      useState('');
    const [completedReportStatus, setCompletedReportStatus] = useState<
      number | undefined
    >(undefined);
    const [reviewerNotes, setReviewerNotes] = useState('');
    const [ecollisionSubmitionDate, setEcollisionSubmitionDate] = useState('');
    const [ecollisionSubmitionTime, setEcollisionSubmittionTime] = useState('');
    const [completedDate, setCompletedDate] = useState('');
    const [completedTime, setCompletedTime] = useState('');
    const [reviewerNotesErrorText, setReviewerNotesErrorText] = useState('');
    const [showECollisionModal, setShowECollisionModal] = useState(false);
    const [showECollisionCallout, setShowECollisionCallout] = useState(false);
    const [
      showDamageEstimateRequiredModal,
      setShowDamageEstimateRequiredModal,
    ] = useState(false);

    useEffect(() => {
      if (stateAuth.auth.access && stateAuth.auth.access.access) {
        dispatch(loadCsrUser({ csrService }));
      }
    }, [stateAuth.auth.access]);

    useEffect(() => {
      if (
        stateAuth.auth.csrUser &&
        !isAdmin(stateAuth) &&
        !isLawEnforcementAdmin(stateAuth) &&
        !isLawEnforcementOfficer(stateAuth)
      ) {
        dispatch(
          pushNotification({
            body: 'You do not have permission to view this page.',
            notificationType: NotificationType.importantGenericError,
          }),
        );
        navigate('/');
      }
    }, [stateAuth.auth.csrUser, navigate]);

    useEffect(() => {
      dispatch(fetchConfiguration(csrService));
    }, []);

    useEffect(() => {
      dispatch(removeAllValidationErrorNotifications());
      dispatch(getAllLEOfficers({ csrService }));

      if (!stateConfig.isLoaded) {
        dispatch(fetchConfiguration(csrService));
      }
    }, [stateAuth.auth.csrUser]);

    useEffect(() => {
      if (isReportApproved() || isReportRejected()) {
        setIsReadOnly(true);
      } else {
        setIsReadOnly(false);
      }
    }, [collision?.reportStatus]);

    useEffect(() => {
      if (
        prevECollisionSubmittedDate.current === null &&
        eCollisionSubmittedDateValue !== null
      ) {
        setShowSubmissionSuccessModal(true);
      }
      prevECollisionSubmittedDate.current = eCollisionSubmittedDateValue;
    }, [eCollisionSubmittedDateValue]);

    useEffect(() => {
      const handleBackNavigation = (event: PopStateEvent) => {
        if (!isSaved) {
          event.preventDefault();
          setShowPendingChangesModal(true);
        }

        dispatch(removeAllNotifications());
      };

      window.addEventListener('popstate', handleBackNavigation);
      window.history.pushState(null, '', window.location.pathname);

      return () => {
        window.removeEventListener('popstate', handleBackNavigation);
      };
    }, [isSaved]);

    useEffect(() => {
      const loadPage = async () => {
        if (collisionId) {
          await dispatch(
            fetchLawEnforcementCollisionById({
              collisionId: collisionId,
              csrService: csrService,
            }),
          );

          await dispatch(
            fetchMediaListByCollisionId({
              id: toInteger(collisionId),
              csrService,
            }),
          );
        }
      };

      loadPage();
    }, [collisionId]);

    useEffect(() => {
      const runSave = async () => {
        if (triggerSave) {
          await save();
          setTriggerSave(false);
        }
      };

      runSave();
    }, [triggerSave]);

    useEffect(() => {
      if (stateLawEnforcementCollisions.collision) {
        setCollision(stateLawEnforcementCollisions.collision);
        setViolations(getViolations(stateLawEnforcementCollisions.collision));
        if (
          stateLawEnforcementCollisions.collision?.policeFileNumber &&
          stateLawEnforcementCollisions.collision.reviewerRegNumber
        ) {
          setEditMode(true);
        }
      }
    }, [stateLawEnforcementCollisions.collision]);

    useEffect(() => {
      if (collision) {
        setFileNumber(collision.policeFileNumber ?? '');
        setReportedDate(collision.reportedDate ?? '');
        if (
          collision.collisionType &&
          stateConfig.configuration.collisionType &&
          stateConfig.configuration.collisionType.length > 0
        ) {
          const c = stateConfig.configuration.collisionType.find(
            (x) => x.id === collision.collisionType,
          )!.formLabel!;
          if (c) setCollisionType(c);
        }

        if (collision.eligibility.length > 0) {
          const answerNodes: React.ReactNode[] = [];
          const questionList = collision.wasHitAndRun
            ? hitAndRunFlowQuestions
            : regularFlowQuestions;

          answerNodes.push(
            <li key={'eligibility-0'}>
              Was your collision a parked vehicle hit and run:{' '}
              {collision.wasHitAndRun ? 'Yes' : 'No'}
            </li>,
          );
          collision.eligibility.map((x, index) => {
            const question = questionList.find((q) => q.id === x.id);
            if (question) {
              answerNodes.push(
                <li key={'eligibility-' + index + 1}>
                  {question.description.replace('?', ':')}{' '}
                  {question.ineligiblityTrigger ? 'No' : 'Yes'}
                </li>,
              );
            }
          });
          setEligibilityResponses(
            <ul className="goa-unordered-list">{answerNodes}</ul>,
          );
        }

        if (
          getConfigurationByDescription(
            stateConfiguration.configuration.collisionType,
            CollisionTypes.ANIMAL,
          )?.id == collision.collisionType
        ) {
          const animalType = stateConfiguration.configuration.animalType.find(
            (x) => x.id == collision.animalType,
          );
          const animalTypeOther =
            stateConfiguration.configuration.otherAnimalType.find(
              (x) => x.id == collision.animalType,
            );

          setCollisionSubType(
            animalType
              ? animalType!.reviewPageLabel!
              : animalTypeOther
                ? animalTypeOther.reviewPageLabel!
                : '',
          );
        }

        if (
          getConfigurationByDescription(
            stateConfiguration.configuration.collisionType,
            CollisionTypes.LEFT_SIDE_ROAD,
          )?.id == collision.collisionType ||
          getConfigurationByDescription(
            stateConfiguration.configuration.collisionType,
            CollisionTypes.RIGHT_SIDE_ROAD,
          )?.id == collision.collisionType
        ) {
          if (
            collision.objectIdentification !=
            getConfigurationByDescription(
              stateConfiguration.configuration.objectIdentification,
              ObjectIdentification.FIXED_OBJECT,
            )?.id
          ) {
            setCollisionSubType('');
          } else if (
            collision.objectIdentification ==
            getConfigurationByDescription(
              stateConfiguration.configuration.objectIdentification,
              ObjectIdentification.FIXED_OBJECT,
            )?.id
          ) {
            setCollisionSubType('Vehicle ran off road and hit an object');
          }
        }

        if (
          getConfigurationByDescription(
            stateConfiguration.configuration.collisionType,
            CollisionTypes.OBJECT_ON_ROADWAY,
          )?.id == collision.collisionType &&
          getConfigurationByDescription(
            stateConfiguration.configuration.objectIdentification,
            ObjectIdentification.FIXED_OBJECT,
          )!.id == collision.objectIdentification
        ) {
          const fixedObject = getConfigurationByDescription(
            stateConfiguration.configuration.objectIdentification,
            ObjectIdentification.FIXED_OBJECT,
          );
          setCollisionSubType(fixedObject ? fixedObject.reviewPageLabel! : '');
        } else if (
          getConfigurationByDescription(
            stateConfiguration.configuration.collisionType,
            CollisionTypes.OBJECT_ON_ROADWAY,
          )?.id == collision.collisionType &&
          getConfigurationByDescription(
            stateConfiguration.configuration.objectIdentification,
            ObjectIdentification.FIXED_OBJECT,
          )!.id != collision.objectIdentification
        ) {
          setCollisionSubType('My vehicle hit a non-fixed object');
        }

        if (
          collision &&
          collision.collisionType &&
          isHitAndRun(collision.collisionType, stateConfig)
        ) {
          setCollisionSubType(
            collision.witnessOrCameraAvailable
              ? 'Witness or a camera provided some information'
              : 'No information is available',
          );
        }

        if (collision.location) {
          setLatLong({
            lat: collision.location.latitude!,
            lng: collision.location.longitude!,
          });
          setZoom(collision.location.zoom!);
          setLocationString(collision.location.locationString!);
          setLocationAdditionalDetails(
            collision.location.additionalLocationDetails ?? '',
          );
        }

        if (collision.dateOfCollision) {
          setCollisionDate(
            moment(collision.dateOfCollision).format('MMMM DD, YYYY'),
          );
        }
        if (collision.timeOfCollision) {
          setCollisionTime(collision.timeOfCollision);
        }
        if (collision.vehicles![0].damage?.damageSelection) {
          setDamageSelectionsList(
            collision.vehicles![0].damage?.damageSelection,
          );
        }

        if (
          collision.specialFacility &&
          collision.collisionType &&
          isHitAndRun(collision.collisionType, stateConfig)
        ) {
          setOccuredInParkingLot(collision.parkingLot ? 'Yes' : 'No');
        } else {
          setOccuredInParkingLot('');
        }

        if (collision.directionOfTravel) {
          setDirectionOfTravel(
            stateConfig.configuration.directionOfTravel.find(
              (x) => x.id == collision.directionOfTravel,
            )?.reviewPageLabel ?? '',
          );
        }
        if (collision.collisionLocation) {
          setIntersectionsAndRailroadCrosings(
            stateConfig.configuration.collisionLocation.find(
              (x) => x.id == collision.collisionLocation,
            )?.reviewPageLabel ?? '',
          );
        }
        if (collision.roadClass) {
          setPrimaryRoadType(
            stateConfig.configuration.roadClasses.find(
              (x) => x.id == collision.roadClass,
            )?.reviewPageLabel ?? '',
          );
        }
        if (collision.specialFacility) {
          setSpecialFacility(
            stateConfig.configuration.specialFacilities.find(
              (x) => x.id == collision.specialFacility,
            )?.reviewPageLabel ?? '',
          );
        }
        if (collision.travelingLane) {
          setTravelingLane(
            stateConfig.configuration.travelingLane.find(
              (x) => x.id == collision.travelingLane,
            )?.reviewPageLabel ?? '',
          );
        }
        if (collision.roadAlignmentA) {
          setRoadSurface(
            stateConfig.configuration.roadAlignmentA.find(
              (x) => x.id == collision.roadAlignmentA,
            )?.reviewPageLabel ?? '',
          );
        }
        if (collision.roadAlignmentB) {
          setRoadStraightOrCurved(
            stateConfig.configuration.roadAlignmentB.find(
              (x) => x.id == collision.roadAlignmentB,
            )?.reviewPageLabel ?? '',
          );
        }
        if (
          collision.vehicles &&
          collision.vehicles.length > 0 &&
          collision.vehicles![0].damage &&
          collision.vehicles![0].damage.damageSelection
        ) {
          let damageSelections = [
            ...collision.vehicles[0].damage?.damageSelection,
          ].sort((x, y) => x.clickTime! - y.clickTime!);
          setDamageList(damageSelections);
          setAdditionalDamageDetails(
            collision.vehicles[0].damage.additionalDamageDetails ?? '',
          );

          setAttachment(
            collision.vehicles[0].damage.attachment
              ? (stateConfig.configuration.attachments.find(
                  (x) =>
                    x.id == Number(collision.vehicles![0].damage!.attachment),
                )?.reviewPageLabel ?? '')
              : '',
          );

          setCarryingLoad(
            collision.vehicles[0].damage.loadDetail
              ? (stateConfig.configuration.loadDetails.find(
                  (x) => x.id == collision.vehicles![0].damage!.loadDetail,
                )?.reviewPageLabel ?? '')
              : '',
          );

          setSpillItems(
            collision.vehicles[0].damage.loadSpillage
              ? (stateConfig.configuration.loadSpillage.find(
                  (x) => x.id == collision.vehicles![0].damage!.loadSpillage,
                )?.reviewPageLabel ?? '')
              : '',
          );
        }

        if (collision.vehicles && collision.vehicles.length > 0) {
          setWeatherCondition(
            collision.vehicles![0].environmentalCondition
              ? stateConfiguration.configuration.weatherCondition.find((x) => {
                  return x.id == collision.vehicles![0].environmentalCondition;
                })?.reviewPageLabel!
              : 'Not applicable',
          );

          setWhichLightCondition(
            collision.vehicles![0].lightConditionA
              ? stateConfiguration.configuration.lightConditionA.find((x) => {
                  return x.id == collision.vehicles![0].lightConditionA;
                })?.reviewPageLabel!
              : 'Not applicable',
          );

          setAnyStreetLights(
            collision.vehicles![0].lightConditionB
              ? stateConfiguration.configuration.lightConditionB.find((x) => {
                  return x.id == collision.vehicles![0].lightConditionB;
                })?.reviewPageLabel!
              : 'Not applicable',
          );

          setRoadCondition(
            collision.vehicles![0].contributingRoadCondition
              ? stateConfiguration.configuration.contributingRoadCondition.find(
                  (x) => {
                    return (
                      x.id == collision.vehicles![0].contributingRoadCondition
                    );
                  },
                )?.reviewPageLabel!
              : 'Not applicable',
          );

          setRoadSurfaceCondition(
            collision.vehicles![0].surfaceCondition
              ? stateConfiguration.configuration.surfaceCondition.find((x) => {
                  return x.id == collision.vehicles![0].surfaceCondition;
                })?.reviewPageLabel!
              : 'Not applicable',
          );

          setTrafficControlDevice(
            collision.vehicles![0].trafficControlDevice
              ? stateConfiguration.configuration.trafficControlDevice.find(
                  (x) => {
                    return x.id == collision.vehicles![0].trafficControlDevice;
                  },
                )?.reviewPageLabel!
              : 'Not applicable',
          );

          setTrafficControlDeviceState(
            collision.vehicles![0].trafficControlCondition
              ? stateConfiguration.configuration.trafficControlCondition.find(
                  (x) => {
                    return (
                      x.id == collision.vehicles![0].trafficControlCondition
                    );
                  },
                )?.reviewPageLabel!
              : 'Not applicable',
          );

          setVehicleCondition(
            collision.vehicles![0].vehicleCondition
              ? stateConfiguration.configuration.vehicleCondition.find((x) => {
                  return x.id == collision.vehicles![0].vehicleCondition;
                })?.reviewPageLabel!
              : 'Not applicable',
          );

          setIsNoContributingFactors(
            collision.vehicles![0].isNoContributingFactors ? 'Yes' : 'No',
          );
        }

        if (collision.vehicles && collision.vehicles.length > 0) {
          setDriver(collision.vehicles[0].driver);
          setVehicle(collision.vehicles[0]);
          setReporterEmail(collision.reporterEmail ?? '');
          setInsuredBy(collision.vehicles[0].insuredBy);
          setIsOwner(collision.vehicles[0].isOwner ?? false);
          setOwner(collision.vehicles[0].owner);
          setDamageEstimateObtained(
            collision.vehicles[0].hasObtainedDamageEstimate ?? false,
          );
          setDamageEstimate(
            collision.vehicles[0].damageEstimate &&
              collision.vehicles[0].damageEstimate > 0
              ? collision.vehicles[0].damageEstimate!.toString()
              : '',
          );
          setInsuredByExpiration(
            collision.vehicles[0].insuredBy?.insurancePolicyExpirationDate ??
              '',
          );
          setPassengers(collision.vehicles[0].passengers ?? []);
        }
        setAdditionalDetails(collision.additionalDetails ?? '');
        setReviewerNotes(collision.reviewerNotes ?? '');
        setReviewer(collision.reviewerRegNumber?.toString() ?? '');

        if (collision.ecollisionSubmissionDate) {
          setEcollisionSubmitionDate(
            moment(collision.ecollisionSubmissionDate).format('MMMM DD, YYYY'),
          );
          setEcollisionSubmittionTime(
            moment(collision.ecollisionSubmissionDate).format('hh:mm A'),
          );
        }
        if (collision.completedDate) {
          setCompletedDate(
            moment(collision.completedDate).format('MMMM DD, YYYY'),
          );
          setCompletedTime(moment(collision.completedDate).format('hh:mm A'));
        }
      }
    }, [collision, stateConfig.configuration.collisionType]);

    useEffect(() => {
      const pois: InitialPointOfImpact[] = [];

      stateConfiguration.configuration.pointOfImpact.forEach((poi: any) => {
        pois.push(new InitialPointOfImpact(poi.id, poi.value));
      });

      setInitialPointOfImpactLocationList(pois);
    }, [stateConfiguration.configuration.pointOfImpact]);

    useEffect(() => {
      if (damageSelectionsList) {
        const damageSelectionsListValues = damageSelectionsList.map((item) =>
          item.pointOfImpactId?.toString(),
        );

        const releventDamageLocations = initialPointOfImpactLocationList.filter(
          (item) => damageSelectionsListValues.includes(item.id.toString()),
        );

        setReleventPointOfImpactLocationList(releventDamageLocations);
      }
    }, [initialPointOfImpactLocationList, damageSelectionsList]);

    useEffect(() => {
      if (collision?.vehicles![0].damage?.damageSelection) {
        if (p5Ref.current) {
          let startCount = 1;

          const p5Instance = new p5(
            damageSketch.sketch(
              containerDiv.current!,
              damageSelectionsList!,
              releventPointOfImpactLocationList,
              startCount,
              () => {},
              false,
              isDesktop
                ? new DesktopDamageSelectorLayoutConfiguration(
                    releventPointOfImpactLocationList,
                  )
                : new MobileDamageSelectorLayoutConfiguration(
                    releventPointOfImpactLocationList,
                  ),
              true,
            ),
            p5Ref.current,
          );

          return () => {
            p5Instance.remove();
          };
        }
      }
    }, [releventPointOfImpactLocationList]);

    const getBase64CanvasImage = (): string | null => {
      if (!containerDiv.current) {
        console.error('Canvas ref is not available.');
        return null;
      }

      const canvas = containerDiv.current.querySelector(
        'canvas',
      ) as HTMLCanvasElement | null;
      if (!canvas) {
        console.error('No canvas element found inside the container.');
        return null;
      }

      return canvas.toDataURL('image/png');
    };

    const isReportUnclaimed = (): boolean => {
      if (collision && collision.reportStatus == 2) {
        return true;
      }
      return false;
    };

    const isReportApprovedAndAssignedToMe = (): boolean => {
      if (
        collision &&
        collision.reportStatus == 4 &&
        collision.reviewerRegNumber &&
        collision.reviewerRegNumber.toString() ==
          (stateAuth.auth.csrUser && stateAuth.auth.csrUser.le_badge
            ? stateAuth.auth.csrUser.le_badge.toString()
            : '')
      ) {
        return true;
      }
      return false;
    };

    const isReportClaimedAndAssignedToMe = (): boolean => {
      if (
        collision &&
        collision.reportStatus == 3 &&
        collision.reviewerRegNumber &&
        collision.reviewerRegNumber.toString() ==
          (stateAuth.auth.csrUser && stateAuth.auth.csrUser.le_badge
            ? stateAuth.auth.csrUser.le_badge.toString()
            : '')
      ) {
        return true;
      }
      return false;
    };

    const isReportCompleted = (): boolean => {
      if (
        collision &&
        collision.reportStatus == 4 &&
        collision.completedDate &&
        !collision.ecollisionSubmissionDate
      ) {
        return true;
      }

      return false;
    };

    const isReportRejected = (): boolean => {
      if (
        collision &&
        collision.reportStatus == 5 &&
        collision.completedDate &&
        !collision.ecollisionSubmissionDate
      ) {
        return true;
      }

      return false;
    };

    const isReportSubmitted = (): boolean => {
      if (collision && collision.ecollisionSubmissionDate) {
        return true;
      }

      return false;
    };

    const isReportApproved = (): boolean | undefined => {
      return collision && collision.reportStatus == 4;
    };

    const getFullName = (): string => {
      if (collision && collision.vehicles && collision.vehicles![0].driver)
        return [
          collision.vehicles![0].driver!.firstName,
          collision.vehicles![0].driver!.middleName ?? '',
          collision.vehicles![0].driver!.lastName,
        ].join(' ');
      else return '';
    };

    const getDamageItems = (): React.ReactNode => {
      const itemNodes: React.ReactNode[] = [];
      let count = 0;
      if (damageList) {
        damageList
          .reduce<DamageSelection[][]>((result, _, index, arr) => {
            if (index % 2 === 0) {
              result.push(arr.slice(index, index + 2));
            }
            return result;
          }, [])
          .map((pair, idx) => {
            const rowKey = `damage-row-${idx}`;

            itemNodes.push(
              <div key={rowKey} className={styles.twoitemrow}>
                {pair.map((item, i) => {
                  const itemKey = `damage-item-${item.pointOfImpactId ?? `${idx}-${i}`}`;

                  const node = (
                    <div key={itemKey}>
                      <div style={{ display: displaySection(!isReadOnly) }}>
                        <NonEditableField
                          key={`field-${item.pointOfImpactId}`}
                          label={`Damage area ${count + 1}`}
                          value={
                            stateConfig.configuration.pointOfImpact.find(
                              (x) => x.id.toString() == item.pointOfImpactId,
                            )?.formLabel!
                          }
                          name={`pointofImpact${i}`}
                        />
                      </div>
                      <div style={{ display: displaySection(isReadOnly) }}>
                        <strong>{`Damage area ${count + 1}`}</strong>
                        <br />
                        {
                          stateConfig.configuration.pointOfImpact.find(
                            (x) => x.id.toString() == item.pointOfImpactId,
                          )?.formLabel!
                        }
                      </div>
                      <GoASpacer vSpacing={'xl'} />
                    </div>
                  );
                  count++;
                  return node;
                })}
              </div>,
            );
          });
      }
      itemNodes.push(
        <div key="first-point-of-impact">
          <div style={{ display: displaySection(!isReadOnly) }}>
            <GoAFormItem mb={'l'} label={'First point of impact'}>
              <GoAInput
                name={`firstPointOfImpact`}
                type="text"
                readonly={true}
                disabled={true}
                width="260px"
                leadingContent={
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '42px',
                      width: '30px',
                      backgroundColor: ' #0081A2',
                      color: 'white',
                      margin: '-7px -12px -12px -12px',
                      borderBottomLeftRadius: '4px',
                      borderTopLeftRadius: '4px',
                    }}
                  >
                    {collision?.vehicles![0].damage?.damageSelection?.findIndex(
                      (x) => x.isInitialPointOfImpact,
                    )
                      ? collision?.vehicles![0].damage?.damageSelection?.findIndex(
                          (x) => x.isInitialPointOfImpact,
                        ) + 1
                      : ''}
                  </div>
                }
                value={
                  stateConfig.configuration.pointOfImpact.find(
                    (x) =>
                      x.id.toString() ==
                      collision?.vehicles![0].damage?.damageSelection?.find(
                        (x) => x.isInitialPointOfImpact,
                      )?.pointOfImpactId,
                  )?.reviewPageLabel!
                }
              ></GoAInput>
            </GoAFormItem>
          </div>
          <div style={{ display: displaySection(isReadOnly) }}>
            <strong>First point of impact</strong>
            <br />
            {collision?.vehicles![0].damage?.damageSelection?.findIndex(
              (x) => x.isInitialPointOfImpact,
            )! + 1}
            {' - '}
            {
              stateConfig.configuration.pointOfImpact.find(
                (x) =>
                  x.id.toString() ==
                  collision?.vehicles![0].damage?.damageSelection?.find(
                    (x) => x.isInitialPointOfImpact,
                  )?.pointOfImpactId,
              )?.reviewPageLabel!
            }
          </div>
        </div>,
      );
      return itemNodes;
    };

    const getPassengerNodes = (): React.ReactNode => {
      const passengerNodes: React.ReactNode[] = [];
      let count = 0;

      passengers.map((passenger, idx) => {
        passengerNodes.push(
          <div key={idx}>
            <h3>Passenger {count + 1} information</h3>
            <GoASpacer vSpacing="xl" />
            <div key={idx + 1} className={styles.twoitemrow}>
              <NonEditableField
                label="Seat/location in vehicle"
                value={
                  stateConfig.configuration.positionInVehicle.find(
                    (x) => x.id == passenger.positionInVehicle,
                  )?.reviewPageLabel!
                }
                name={`passengerFirstName${count}`}
                isReadOnly={isReadOnly}
              />
              <NonEditableField
                label="Safety equipment used"
                value={
                  stateConfig.configuration.safetyEquipment.find(
                    (x) => x.id == passenger.safetyEquipmentUsed,
                  )?.reviewPageLabel!
                }
                name={`passengerFirstName${count}`}
                isReadOnly={isReadOnly}
              />
            </div>
            <div key={idx + 2} className={styles.twoitemrow}>
              <NonEditableField
                label="Last name"
                value={passenger.lastName}
                name={`passengerLastName${count}`}
                isReadOnly={isReadOnly}
              />
              <NonEditableField
                label="First name"
                value={passenger.firstName}
                name={`passengerFirstName${count}`}
                isReadOnly={isReadOnly}
              />
            </div>
            <div key={idx + 3} className={styles.twoitemrow}>
              <NonEditableField
                label="Gender"
                value={
                  stateConfig.configuration.genders.find(
                    (x) => x.id.toString() == passenger.gender,
                  )?.reviewPageLabel!
                }
                name={`passengerSex${count}`}
                isReadOnly={isReadOnly}
              />
              <NonEditableField
                label="Date of birth"
                value={
                  passenger.dateOfBirth
                    ? moment(passenger.dateOfBirth).format('MMM DD, YYYY')
                    : ''
                }
                name={`passengerDateOfBirth${count}`}
                isReadOnly={isReadOnly}
              />
            </div>
            <NonEditableField
              label="Street address"
              value={passenger.address.address ?? ''}
              name={`passengerStreetAddress${count}`}
              fullWidth={true}
              isReadOnly={isReadOnly}
            />
            <div key={idx + 4} className={styles.twoitemrow}>
              <NonEditableField
                label="City or town"
                value={passenger.address.city ?? ''}
                name={`passengerCityOrTown${count}`}
                isReadOnly={isReadOnly}
              />
              <NonEditableField
                label="Province"
                value={passenger.address.province ?? ''}
                name={`passengerProvince${count}`}
                isReadOnly={isReadOnly}
              />
            </div>
            <div key={idx + 5} className={styles.twoitemrow}>
              <NonEditableField
                label="Country"
                value={passenger.address.country ?? ''}
                name={`passengerCountry${count}`}
                isReadOnly={isReadOnly}
              />
              <NonEditableField
                label="Postal code"
                value={passenger.address.postalCode ?? ''}
                name={`passengerPostalCode${count}`}
                isReadOnly={isReadOnly}
              />
            </div>
            <div key={idx + 6} className={styles.twoitemrow}>
              <NonEditableField
                label="Phone number"
                value={passenger.homePhoneNumber ?? ''}
                name={`passengerPhoneNumber${count}`}
                isReadOnly={isReadOnly}
              />
              <NonEditableField
                label="Email address"
                value={passenger.workPhoneNumber ?? ''}
                name={`passengerEmail${count}`}
                requirement="optional"
                isReadOnly={isReadOnly}
              />
            </div>
          </div>,
        );

        count++;
      });

      return passengerNodes;
    };

    const save = async () => {
      if (collision) {
        let updatedCollision: Collision = { ...collision };

        if (latLng && zoom) {
          updatedCollision.location = {
            ...updatedCollision.location,
            latitude: latLng.lat,
            longitude: latLng.lng,
            locationString: locationString,
            zoom: zoom,
          };
        }

        if (
          updatedCollision.vehicles &&
          updatedCollision.vehicles.length > 0 &&
          updatedCollision.vehicles[0].insuredBy
        ) {
          updatedCollision.vehicles = [
            {
              ...updatedCollision.vehicles?.[0],
              damageEstimate: damageEstimate ? +damageEstimate : undefined,
            },
          ];

          updatedCollision.vehicles[0].insuredBy = {
            ...updatedCollision.vehicles[0].insuredBy,
            insurancePolicyNumber: insuredBy?.insurancePolicyNumber ?? '',
            companyName: insuredBy?.companyName ?? '',
            insurancePolicyExpirationDate: insuredByExpiration,
          };

          updatedCollision.policeFileNumber = fileNumber;
        }

        if (reviewerNotes) {
          updatedCollision.reviewerNotes = reviewerNotes;
        }

        await dispatch(updateLECollision(updatedCollision));
        await dispatch(publishLECollision({ csrService }));

        setIsSaved(true);
      }

      if (navigateToDashboard) {
        navigate('/law/dashboard');
      }
    };

    return (
      <div>
        {/*  ecollision error */}
        <GoAModal
          calloutVariant="emergency"
          open={showECollisionModal}
          maxWidth="400px"
          actions={
            <GoAButtonGroup alignment="end">
              <GoAButton
                type="primary"
                onClick={() => {
                  setShowECollisionModal(false);
                }}
              >
                Close
              </GoAButton>
            </GoAButtonGroup>
          }
        >
          There was a problem submitting this report to e-Collision
        </GoAModal>
        {/*  success submited */}
        <GoAModal
          heading="Collision report has been successfully submitted to eCollision"
          open={showSubmissionSuccessModal && !showECollisionCallout}
          calloutVariant="success"
          maxWidth="480px"
          actions={
            <GoAButton onClick={() => setShowSubmissionSuccessModal(false)}>
              Close
            </GoAButton>
          }
        >
          The collision report record will remain visible on the dashboard under
          the 'Completed' tab. Any future edits must be made directly in
          eCollision if needed
        </GoAModal>

        {/* unsaved changes modal */}

        <GoAModal
          open={showPendingChangesModal && !showECollisionCallout}
          maxWidth="500px"
          heading="You have unsaved changes or updates to this collision report"
          role="alertdialog"
          calloutVariant="important"
          actions={
            <GoAButtonGroup alignment="end">
              <GoAButton onClick={() => setShowPendingChangesModal(false)}>
                Cancel
              </GoAButton>
            </GoAButtonGroup>
          }
        >
          You've updated or changed a field that needs to be saved. Close this
          window and check your changes.
        </GoAModal>

        {/* confirm location modal */}

        <GoAModal
          open={showMapEditModal}
          maxWidth="600px"
          actions={
            <GoAButtonGroup alignment="end">
              <GoAButton
                type="secondary"
                onClick={() => {
                  setNewLatLng(undefined);
                  setNewZoom(undefined);
                  setNewLocationString('');
                  setIsSaved(true);
                  setShowMapEditModal(false);
                }}
              >
                Cancel
              </GoAButton>
              <GoAButton
                onClick={() => {
                  setLatLong(newLatLng);
                  setZoom(newZoom);
                  setLocationString(newLocationString);
                  setIsSaved(false);
                  setShowMapEditModal(false);
                  setTriggerSave(true);
                  dispatch(
                    pushNotification({
                      body: `You change or updates have been saved`,
                      notificationType: NotificationType.informationGeneric,
                    }),
                  );
                }}
              >
                Confirm location
              </GoAButton>
            </GoAButtonGroup>
          }
        >
          <CollisionMapComponent
            apiKey={apiKey}
            className={styles.map}
            defaultLocation={DEFAULT_LOCATION}
            updateLatLng={(latLng) => {
              setNewLatLng(latLng);
            }}
            updateInAlberta={(inAlberta) => {
              if (!inAlberta) {
                setNewLatLng(undefined);
                setNewZoom(undefined);
                setNewLocationString('');
                alert('Location must be within Alberta');
              }
            }}
            updateLocationString={(locationString) => {
              setNewLocationString(locationString);
            }}
            updateZoom={(zoom) => {
              setNewZoom(zoom);
            }}
            zoom={newZoom ?? 15}
            latLng={{
              lat: newLatLng ? newLatLng.lat : 0,
              lng: newLatLng ? newLatLng.lng : 0,
            }}
          />
        </GoAModal>

        {/* claim model */}

        <GoAModal
          maxWidth="480px"
          heading="Claim collision report"
          open={showClaimModal}
        >
          Enter the police file number associated with this collision report to
          start reviewing it.
          <GoASpacer vSpacing="l" />
          <GoAFormItem label="Police file number">
            <GoAInputText
              value={fileNumber}
              name="policeFileNumber"
              onChange={(item, value) => {
                if (value) setFileNumberText(value);
              }}
            ></GoAInputText>
          </GoAFormItem>
          <GoASpacer vSpacing="xl" />
          <GoAButtonGroup alignment="end">
            <GoAButton
              type="secondary"
              onClick={() => setShowClaimModal(false)}
            >
              Cancel
            </GoAButton>
            <GoAButton
              type="primary"
              onClick={() => {
                const reviewerId = stateAuth.auth.csrUser?.le_badge;
                setEditMode(true);
                setShowClaimModal(false);
                setReviewer(reviewerId ?? '');
                setFileNumber(fileNumberText);
                dispatch(
                  updateLECollisionStatus({
                    collisionId: collision!.id!.toString(),
                    status: '3',
                    csrService,
                  }),
                )
                  .unwrap()
                  .then(() => {
                    setTriggerSave(true);
                    dispatch(
                      pushNotification({
                        body: `Report ID#${collision!.id} has been claimed by ${
                          lawEnforcementOfficers.reviewers?.find(
                            (x) => x.badgeNumber == reviewerId,
                          )?.name
                        }`,
                        notificationType: NotificationType.informationGeneric,
                      }),
                    );
                  })
                  .catch((error: any) => {
                    dispatch(
                      pushNotification({
                        body: error.message,
                        notificationType:
                          NotificationType.importantGenericError,
                      }),
                    );
                  });
              }}
            >
              Claim
            </GoAButton>
          </GoAButtonGroup>
        </GoAModal>

        {/* damage estimate required modal */}

        <GoAModal
          maxWidth="480px"
          heading="Enter a damage estimate amount to complete the report"
          open={showDamageEstimateRequiredModal}
          actions={
            <GoAButton
              onClick={() => setShowDamageEstimateRequiredModal(false)}
            >
              Close
            </GoAButton>
          }
          calloutVariant="important"
        >
          If the damage in the photos appears to meet the reportable threshold,
          enter an estimated amount of at least $5000. If the damage in the
          photos appears to be below the threshold, you should reject the
          report.
        </GoAModal>

        {/* complete report modal */}

        <GoAModal
          maxWidth="480px"
          heading="Complete collision report review"
          open={showCompleteModal}
        >
          Approving this report will send an email to the driver and create a
          damage sticker for them to download. If the report is not approved,
          the driver will receive an email informing them and instructing them
          to resubmit, either in person or online via the online service.
          <GoASpacer vSpacing="l" />
          <GoAFormItem label="Decisions">
            <GoARadioGroup
              value={
                completedReportStatus ? completedReportStatus.toString() : ''
              }
              onChange={(item, value) => {
                setCompletedReportStatus(parseInt(value));
              }}
              name="decisions"
            >
              <GoARadioItem value={'4'} label="Approved" />
              <GoARadioItem value={'5'} label="Not approved" />
            </GoARadioGroup>
          </GoAFormItem>
          <GoASpacer vSpacing="l" />
          <GoAFormItem
            label="Reviewer notes (for example reason for decision, etc.)"
            error={reviewerNotesErrorText}
          >
            <GoATextArea
              value={reviewerNotes}
              error={reviewerNotesErrorText.length > 0}
              onChange={(item, value) => {
                if (value.length <= 150) {
                  setReviewerNotesErrorText('');
                }
                setReviewerNotes(value);
              }}
              name="reviewerNotes"
              maxCount={150}
              countBy="character"
            ></GoATextArea>
          </GoAFormItem>
          <GoASpacer vSpacing="xl" />
          <GoAButtonGroup alignment="end">
            <GoAButton
              type="secondary"
              onClick={() => setShowCompleteModal(false)}
            >
              Cancel
            </GoAButton>
            <GoAButton
              type="primary"
              onClick={() => {
                if (
                  completedReportStatus == 4 ||
                  (completedReportStatus == 5 && reviewerNotes.length <= 150)
                ) {
                  const saveAndComplete = async (
                    completedReportStatus: number,
                  ) => {
                    if (
                      reviewerNotes.length > 0 &&
                      reviewerNotes.length <= 150
                    ) {
                      const base64Image = getBase64CanvasImage();

                      await dispatch(
                        uploadCanvasImage({
                          collisionId: collision!.id!.toString(),
                          vehicleId: '1',
                          base64Image: base64Image ?? '',
                          csrService,
                        }),
                      ).unwrap();
                      await save();
                      await dispatch(
                        updateLECollisionStatus({
                          collisionId: collision!.id!.toString(),
                          status: completedReportStatus.toString(),
                          csrService,
                        }),
                      );

                      setShowCompleteModal(false);
                      setReviewerNotesErrorText('');
                    } else {
                      setReviewerNotesErrorText(
                        'Reviewer notes must be between 1 and 150 characters',
                      );
                    }
                  };

                  saveAndComplete(completedReportStatus);
                }
              }}
            >
              Complete report
            </GoAButton>
          </GoAButtonGroup>
        </GoAModal>
        <GoASpacer vSpacing="l" />
        <GoAButton
          type="tertiary"
          leadingIcon="arrow-back"
          onClick={() => {
            if (isSaved) {
              dispatch(removeAllNotifications());
              navigate('/law/dashboard');
            } else {
              setShowPendingChangesModal(true);
            }
          }}
        >
          Back to Collision Reports
        </GoAButton>
        <GoASpacer vSpacing="xl" />
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              display: 'flex',
              gap: '15px',
              alignItems: 'end',
            }}
          >
            <GoABadge
              content={`Report ID  #${collision?.id}`}
              type={'information'}
            />

            {collision && collision.reportStatus && collision.reportedDate
              ? getReportStatus(
                  collision.reportStatus,
                  new Date(collision.reportedDate),
                )
              : undefined}
          </span>
          <div style={{ width: '90px' }}>
            <GoAButtonGroup alignment="start">
              <div style={{ display: displaySection(isReportUnclaimed()) }}>
                <GoAButton
                  width="80px"
                  onClick={() => {
                    setShowClaimModal(true);
                  }}
                >
                  Claim
                </GoAButton>
              </div>
              <div
                style={{
                  display: displaySection(isReportClaimedAndAssignedToMe()),
                }}
              >
                <GoAButtonGroup alignment="end">
                  <GoAButton
                    type="primary"
                    onClick={() => {
                      if (!isSaved) {
                        setShowPendingChangesModal(true);
                      } else {
                        if (damageEstimate) {
                          setShowCompleteModal(true);
                        } else {
                          setShowDamageEstimateRequiredModal(true);
                        }
                      }
                    }}
                  >
                    Complete
                  </GoAButton>
                </GoAButtonGroup>
              </div>

              <div
                style={{
                  display: displaySection(
                    isReportCompleted() &&
                      !isReportRejected() &&
                      isReportApprovedAndAssignedToMe(),
                  ),
                }}
              >
                <GoAButton
                  type="secondary"
                  onClick={() => {
                    const sendToEcollision = async () => {
                      try {
                        const result = await dispatch(
                          submitLECollisionToEcollision({ csrService }),
                        ).unwrap();
                        setShowECollisionCallout(false);
                      } catch (error: unknown) {
                        setShowECollisionModal(true);
                        setShowECollisionCallout(true);
                      }
                    };
                    sendToEcollision();
                  }}
                >
                  Send to E-Collision
                </GoAButton>
              </div>
            </GoAButtonGroup>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              display: 'flex',
              gap: '5px',
              justifyContent: 'space-between',
              alignContent: 'center',
              alignItems: 'center',
            }}
          >
            <h2 style={{ marginTop: '0px' }}>{getFullName()}</h2>
            <span className="bluedot"></span>
            <span
              style={{
                font: 'var(--goa-typography-body-l)',
                color: '#666666',
              }}
            >
              {moment(collisionDate).format('MMMM DD, YYYY')}
            </span>
          </span>
          <div style={{ display: displaySection(isReportUnclaimed()) }}>
            Unassigned
          </div>
          <div style={{ display: displaySection(!isReportUnclaimed()) }}>
            <span
              style={{
                display: 'flex',
                gap: '5px',
                justifyContent: 'space-between',
                alignContent: 'center',
                alignItems: 'center',
                font: 'var(--goa-typography-body-m)',
                color: '#666666',
              }}
            >
              <span>
                {
                  lawEnforcementOfficers.reviewers?.find(
                    (x) => x.badgeNumber?.toString() == reviewer?.toString(),
                  )?.name
                }
              </span>
              <span
                style={{
                  display: displaySection(reviewer != '' && fileNumber != ''),
                }}
                className="bluedot"
              ></span>
              <span>{fileNumber}</span>
            </span>
          </div>
        </div>

        <GoASpacer vSpacing="xl" />
        <div style={{ maxWidth: '630px' }}>
          <div
            style={{
              display: displaySection(showECollisionCallout),
            }}
          >
            <GoACallout
              type="emergency"
              heading="There was a problem submitting this report to e-Collision"
              size="medium"
            >
              We have encountered a technical issue when submitting the report
              to eCollision. Please try again at a later time
            </GoACallout>
          </div>
          <div
            style={{
              display: displaySection(
                (isReportCompleted() || isReportRejected()) &&
                  !showECollisionCallout,
              ),
            }}
          >
            <GoACallout
              type={isReportApproved() ? 'success' : 'emergency'}
              size="medium"
            >
              {isReportApproved()
                ? 'Approved by '
                : 'Marked as not approved by '}
              {
                lawEnforcementOfficers.reviewers?.find(
                  (x) => x.badgeNumber == reviewer,
                )?.name
              }{' '}
              on {completedDate} at {completedTime}.{' '}
              {isReportApproved()
                ? 'This report is ready to send to e-collision'
                : 'See reviewer notes below.'}
            </GoACallout>

            <div style={{ display: displaySection(reviewerNotes != '') }}>
              <GoADivider />
              <GoASpacer vSpacing="3xl" />
              <GoAContainer accent="thin">
                <strong>Reviewer Notes</strong>
                <GoASpacer vSpacing="m" />
                {reviewerNotes}
              </GoAContainer>
            </div>
          </div>
          <div style={{ display: displaySection(isReportSubmitted()) }}>
            <GoACallout
              type="success"
              heading={`Submitted to eCollision by ${
                lawEnforcementOfficers.reviewers?.find(
                  (x) => x.badgeNumber == reviewer,
                )?.name
              } on ${ecollisionSubmitionDate} at ${ecollisionSubmitionTime}`}
              size="medium"
            >
              This is a read-only copy of the collision report. Any future edits
              must be made in eCollision if needed.
            </GoACallout>
            <div style={{ display: displaySection(reviewerNotes != '') }}>
              <GoADivider />
              <GoASpacer vSpacing="3xl" />
              <GoAContainer
                accent="thin"
                title="Reviewer notes"
                maxWidth="6300px"
              >
                {reviewerNotes}
              </GoAContainer>
            </div>
          </div>
          <div
            style={{
              display: displaySection(
                collision != undefined &&
                  getViolations(collision).length > 0 &&
                  !isReportCompleted(),
              ),
            }}
          >
            <GoACallout
              type="emergency"
              heading="This driver has violations"
              size="medium"
            >
              <span>{violations.join(', ')}</span>
            </GoACallout>
          </div>
        </div>

        <GoADivider />

        <GoASpacer vSpacing="xl" />
        <h3>Driver eligibility response</h3>
        <GoASpacer vSpacing="l" />

        <div style={{ display: displaySection(!isReadOnly) }}>
          <GoAAccordion maxWidth="670px" heading="Driver eligibility responses">
            {eligibilityResponses}
          </GoAAccordion>
        </div>
        <div style={{ display: displaySection(isReadOnly) }}>
          {eligibilityResponses}
        </div>
        <SectionComponent header="1. Collision type">
          <NonEditableField
            label="Collision type"
            value={collisionType}
            name="collisionType"
            fullWidth={true}
            isReadOnly={isReadOnly}
          />
          <NonEditableField
            label="Collision sub type"
            value={collisionSubType}
            name="collisionSubType"
            fullWidth={true}
            isReadOnly={isReadOnly}
          />
        </SectionComponent>
        <SectionComponent header="2. Location">
          <div>
            <div style={{ display: displaySection(!isReadOnly) }}>
              <GoAFormItem label="Collision location">
                <span style={{ display: 'flex', gap: '10px' }}>
                  <GoAInputText
                    name="collisionLocation"
                    width={!editMode ? '630px' : '495px'}
                    value={locationString ?? ''}
                    disabled={true}
                  />
                  <div
                    style={{
                      display: displaySection(
                        editMode && isReportClaimedAndAssignedToMe(),
                      ),
                    }}
                  >
                    <GoAButton
                      width="130px"
                      onClick={() => {
                        setNewLatLng(latLng);
                        setNewZoom(zoom);
                        setNewLocationString(locationString);
                        setShowMapEditModal(true);
                        setShowMapAccordian(false);
                      }}
                    >
                      Edit location
                    </GoAButton>
                  </div>
                </span>

                <GoASpacer vSpacing="xl" />

                <div>
                  <GoAAccordion
                    maxWidth={'670px'}
                    heading="View map"
                    open={showMapAccordian}
                    onChange={(isOpen) => {
                      setShowMapAccordian(isOpen);
                    }}
                  >
                    <ReviewMapComponent
                      apiKey={apiKey}
                      lat={latLng ? latLng.lat : 0}
                      lng={latLng ? latLng.lng : 0}
                      zoom={zoom ?? 15}
                      className={styles.map}
                    />
                  </GoAAccordion>
                </div>
              </GoAFormItem>
              <div
                style={{
                  display: displaySection(locationAdditionalDetails != ''),
                }}
              >
                <GoASpacer vSpacing="m" />
                <GoAFormItem
                  label="Additional location details"
                  requirement="optional"
                >
                  <GoATextArea
                    name="locationAdditionalDetails"
                    width="630px"
                    value={locationAdditionalDetails}
                    disabled={true}
                  />
                </GoAFormItem>
              </div>
            </div>
            <div style={{ display: displaySection(isReadOnly) }}>
              <strong>Address</strong>
              <GoASpacer vSpacing="s" />
              {locationString}
            </div>
          </div>
        </SectionComponent>
        <SectionComponent header="3. Collision date and time">
          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Date"
              value={collisionDate}
              name="collisionDate"
              trailingIcon="calendar"
              isReadOnly={isReadOnly}
            />
            <NonEditableField
              label="Time"
              value={collisionTime}
              name="collisionTime"
              trailingIcon="time"
              isReadOnly={isReadOnly}
            />
          </div>
        </SectionComponent>
        <SectionComponent header="4. Direction of travel and road details">
          <div className={styles.twoitemrow}>
            {collision &&
            collision.collisionType &&
            !isHitAndRun(collision.collisionType, stateConfig) ? (
              <div>
                <NonEditableField
                  label="Direction of travel"
                  value={directionOfTravel}
                  name="directionOfTravel"
                  isReadOnly={isReadOnly}
                />
              </div>
            ) : (
              <NonEditableField
                label="Hit and run occured in parking lot"
                value={occuredInParkingLot}
                name="occuredInParkingLot"
                isReadOnly={isReadOnly}
              />
            )}
            <NonEditableField
              label="Intersections and railroad crossings"
              value={instersectionsAndRailroadCrosings}
              name="intersectionsAndRailroadCrosings"
              isReadOnly={isReadOnly}
            />
          </div>
          {occuredInParkingLot != 'Yes' ? (
            <div>
              <h3 style={{ marginBottom: '30px' }}>Road types</h3>
              <NonEditableField
                label="Primary road type"
                value={primaryRoadType}
                name="primaryRoadType"
                fullWidth={true}
                isReadOnly={isReadOnly}
              />
              <div className={styles.twoitemrow}>
                <NonEditableField
                  label="Traveling lane from median"
                  value={travelingLane}
                  name="travelingLane"
                  visible={travelingLane != undefined}
                  isReadOnly={isReadOnly}
                />
                <NonEditableField
                  label="Secondary road type"
                  value={specialFacility}
                  name="secondaryRoadType"
                  fullWidth={travelingLane == undefined}
                  isReadOnly={isReadOnly}
                />
              </div>
              <h3 style={{ marginBottom: '30px' }}>Road characteristics</h3>
              <div className={styles.twoitemrow}>
                <NonEditableField
                  label="Road surface"
                  value={roadSurface}
                  name="roadSurface"
                  isReadOnly={isReadOnly}
                />
                <NonEditableField
                  label="Road straight or curved"
                  value={roadStraighOrCurved}
                  name="roadStraightOrCurved"
                  isReadOnly={isReadOnly}
                />
              </div>
            </div>
          ) : null}
        </SectionComponent>
        <SectionComponent header="5. Vehicle Damage">
          <div
            style={{
              width: '600px',
              display: 'flex',
              alignContent: 'center',
              justifyContent: 'center',
            }}
          >
            <div ref={containerDiv} className="damage-vehicle">
              <div ref={p5Ref} />
            </div>
          </div>
          <GoASpacer vSpacing="l" />

          <div>{getDamageItems()}</div>

          <div
            style={{
              display: displaySection(
                stateLawEnforcementCollisions.collision?.vehicles![0].damage
                  ?.attachment! != undefined,
              ),
            }}
          >
            <GoASpacer vSpacing="xl" />
            <h3>Other damage areas</h3>
            <GoASpacer vSpacing="xl" />
            Other damage details are below if they include load/trailer
            attachment
            <GoASpacer vSpacing="m" />
            <GoASpacer vSpacing="xl" />
            <div className={styles.twoitemrow}>
              <NonEditableField
                label="Load/trailer attachment"
                value={attachment}
                name="attachment"
                isReadOnly={isReadOnly}
              />
              <NonEditableField
                label="Was attachment carrying load?"
                value={carryingLoad}
                name="carryingLoad"
                isReadOnly={isReadOnly}
              />
            </div>
            <NonEditableField
              label="Did attachment lose/spill load?"
              value={spillItems}
              name="carryingLoad"
              isReadOnly={isReadOnly}
            />
          </div>

          {!isReadOnly ? (
            <GoAFormItem label="Additional damage details">
              <GoATextArea
                name="additionalDamageDetails"
                width="630px"
                value={additionalDamageDetails}
                disabled={true}
              />
            </GoAFormItem>
          ) : additionalDamageDetails ? (
            <div>
              <GoASpacer vSpacing="xl" />
              <strong>Additional damage details</strong>
              <div>{additionalDamageDetails}</div>
            </div>
          ) : (
            ''
          )}
        </SectionComponent>
        <SectionComponent
          header={
            <div>
              <div className={styles.flexboxheader}>
                6. Photo Uploads
                <span style={{ flexGrow: 1, display: 'flex' }}></span>
                <GoAButton
                  leadingIcon="arrow-down"
                  type="tertiary"
                  onClick={() => {
                    dispatch(
                      fetchMediaZipByCollisionId({
                        id: collisionId ? toInteger(collisionId) : 0,
                        csrService,
                      }),
                    );
                  }}
                >
                  Download all media (.zip)
                </GoAButton>
              </div>
            </div>
          }
        >
          <div style={{ display: displaySection(!isReadOnly) }}>
            <GoAAccordion heading="Vehicle damage photos" maxWidth={'600px'}>
              <div className={styles.accordiantext}>
                Click a thumbnail to view in full
              </div>
              <MediaCopyGenerater mediaState={stateMedia} />
            </GoAAccordion>
          </div>
          <div style={{ display: displaySection(isReadOnly) }}>
            <strong>Vehicle damage photos</strong>
            <GoASpacer vSpacing="xl" />
            <MediaCopyGenerater mediaState={stateMedia} />
          </div>
        </SectionComponent>
        <GoASpacer vSpacing="xl" />
        <SectionComponent header="7. Contributing factors">
          {collision &&
          collision.collisionType &&
          !isHitAndRun(collision.collisionType, stateConfig) ? (
            <div>
              <h3>Weather conditions</h3>
              <GoASpacer vSpacing="xl" />
              <NonEditableField
                label="Which weather condition was a contributing factor in your collision?"
                value={weatherCondition}
                name="weatherCondition"
                isReadOnly={isReadOnly}
                fullWidth={true}
              />
              <h3>Light conditions</h3>
              <GoASpacer vSpacing="xl" />
              <NonEditableField
                label="Which light condition was the contributing factor in your collision"
                value={whichLightCondition}
                name="weatherCondition"
                isReadOnly={isReadOnly}
                fullWidth={true}
              />

              <NonEditableField
                label="Were there any street lights?"
                value={anyStreetLights}
                name="anyStreelight"
                isReadOnly={isReadOnly}
              />

              <h3>Road and surface conditions</h3>
              <GoASpacer vSpacing="xl" />

              <NonEditableField
                label="Which road condition was the contributing factor in your collision?"
                value={roadCondition}
                name="weatherCondition"
                isReadOnly={isReadOnly}
                fullWidth={true}
              />

              <NonEditableField
                label="Which road surface condition was the contributing factor in your collision?"
                value={roadSurfaceCondition}
                name="weatherCondition"
                isReadOnly={isReadOnly}
                fullWidth={true}
              />

              <h3>Traffic control device</h3>
              <GoASpacer vSpacing="xl" />

              <NonEditableField
                label="Which traffic control device was the contributing factor in your collision?"
                value={trafficControlDevice}
                name="trafficControlDevice"
                isReadOnly={isReadOnly}
                fullWidth={true}
              />
              <NonEditableField
                label="Condition of traffice device?"
                value={trafficControlDeviceState}
                name="trafficControlDeviceState"
                isReadOnly={isReadOnly}
              />

              <h3>Vehicle Conditions</h3>
              <GoASpacer vSpacing="xl" />
              <NonEditableField
                label="Which vehicle condition was the contributing factor in your collision?"
                value={vehicleCondition}
                name="vehicleCondition"
                isReadOnly={isReadOnly}
                fullWidth={true}
              />
              <h3>None of the above</h3>
              <GoASpacer vSpacing="xl" />
              <NonEditableField
                label="None of the above"
                value={isNoContributingFactors}
                name="isNoContributingFactors"
                isReadOnly={isReadOnly}
              />
            </div>
          ) : (
            'Contributing factors not applicable for hit and run collisions'
          )}
        </SectionComponent>
        <GoASpacer vSpacing="xl" />
        <SectionComponent header="8. Driver and vehicle information">
          <h3>Personal information</h3>
          <GoASpacer vSpacing="xl" />
          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Last name"
              value={driver?.lastName ?? ''}
              name="driverLastName"
              isReadOnly={isReadOnly}
            />
            <NonEditableField
              label="First name"
              value={driver?.firstName ?? ''}
              name="driverFirstName"
              isReadOnly={isReadOnly}
            />
          </div>
          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Driver's licence number"
              value={driver?.licenceNumber ?? ''}
              name="driverLicenceNumber"
              isReadOnly={isReadOnly}
            />
            <NonEditableField
              label="Date of birth"
              value={
                driver?.dateOfBirth
                  ? moment(driver.dateOfBirth).format('MMMM DD, YYYY')
                  : ''
              }
              name="driverDateOfBirth"
              isReadOnly={isReadOnly}
            />
          </div>
          <h3>Contact information</h3>
          <GoASpacer vSpacing="xl" />
          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Street Address"
              value={driver?.address.address ?? ''}
              name="streetAddress"
              isReadOnly={isReadOnly}
            />
            <NonEditableField
              label="City or town"
              value={driver?.address.city ?? ''}
              name="city or town"
              isReadOnly={isReadOnly}
            />
          </div>
          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Province or state"
              value={driver?.address.province ?? ''}
              name="province or State"
              isReadOnly={isReadOnly}
            />
            <NonEditableField
              label="Postal or zip code"
              value={driver?.address.postalCode ?? ''}
              name="postalOrZipCode"
              isReadOnly={isReadOnly}
            />
          </div>
          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Primary phone"
              value={driver?.homePhoneNumber ?? ''}
              name="primaryPhoneNumber"
              isReadOnly={isReadOnly}
            />
            <NonEditableField
              requirement="optional"
              label="Alternate phone"
              value={driver?.workPhoneNumber ?? ''}
              name="alternatePhoneNumber"
              isReadOnly={isReadOnly}
            />
          </div>
          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Email adderess"
              value={reporterEmail}
              name="email"
              isReadOnly={isReadOnly}
            />
          </div>
          <h3>Safety information</h3>
          <GoASpacer vSpacing="xl" />

          <NonEditableField
            label="Driver safety equipment used"
            value={
              stateConfig.configuration.safetyEquipment.find(
                (x) => driver?.safetyEquipmentUsed,
              )?.reviewPageLabel ?? ''
            }
            name="driverSafetyEquipmentUsed"
            fullWidth={true}
            isReadOnly={isReadOnly}
          />
          <h3>Vehicle Information</h3>
          <GoASpacer vSpacing="xl" />

          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Licence plate number"
              value={vehicle?.licencePlateNumber ?? ''}
              name="licencePlateNumber"
              isReadOnly={isReadOnly}
            />
            <NonEditableField
              label="Province"
              value={'AB'}
              name="province"
              isReadOnly={isReadOnly}
            />
          </div>
          <NonEditableField
            label="Vehicle identification number (VIN#)"
            value={vehicle?.vinNumber ?? ''}
            name="vinNumber"
            isReadOnly={isReadOnly}
            fullWidth={true}
          />
          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Year"
              value={vehicle?.year ?? ''}
              name="vehicleYear"
              isReadOnly={isReadOnly}
            />
            <NonEditableField
              label="Make"
              value={vehicle?.make ?? ''}
              name="vehicleMake"
              isReadOnly={isReadOnly}
            />
          </div>

          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Model"
              value={vehicle?.model ?? ''}
              name="vehicleModel"
              isReadOnly={isReadOnly}
            />
            <NonEditableField
              label="Vehicle type"
              value={
                stateConfig.configuration.objectIdentification?.find(
                  (x) => x.id == toInteger(vehicle?.type),
                )?.reviewPageLabel!
              }
              name="vehicleType"
              isReadOnly={isReadOnly}
            />
          </div>
          <NonEditableField
            label="Vehicle colour"
            value={
              stateConfig.configuration.vehicleColours.find(
                (x) => x.id == toInteger(vehicle?.color),
              )?.value ?? ''
            }
            name="vehicleColour"
            isReadOnly={isReadOnly}
          />

          <h3>Owner information</h3>
          <GoASpacer vSpacing="xl" />
          <NonEditableField
            label="Are you the registered owner of this vehicle?"
            value={isOwner ? 'Yes' : 'No'}
            name="areYouOwner"
            fullWidth={true}
            isReadOnly={isReadOnly}
          />
          <div
            style={{
              display: displaySection(!isOwner),
            }}
          >
            <div className={styles.twoitemrow}>
              <NonEditableField
                label="Last name"
                value={owner?.lastName ?? ''}
                name="ownerLastName"
                isReadOnly={isReadOnly}
              />
              <NonEditableField
                label="First name"
                value={owner?.firstName ?? ''}
                name="ownerFirstName"
                isReadOnly={isReadOnly}
              />
            </div>
            <div className={styles.twoitemrow}>
              <NonEditableField
                label="Street Address"
                value={owner?.address.address ?? ''}
                name="ownerStreetAddress"
                isReadOnly={isReadOnly}
              />
              <NonEditableField
                label="City or town"
                value={owner?.address.city ?? ''}
                name="ownerCityOrTown"
                isReadOnly={isReadOnly}
              />
            </div>
            <div className={styles.twoitemrow}>
              <NonEditableField
                label="Province or state"
                value={owner?.address.province ?? ''}
                name="ownerProvinceOrState"
                isReadOnly={isReadOnly}
              />
              <NonEditableField
                label="Postal or zip code"
                value={owner?.address.postalCode ?? ''}
                name="ownerPostalOrZipCode"
                isReadOnly={isReadOnly}
              />
            </div>
            <div className={styles.twoitemrow}>
              <NonEditableField
                label="Primary phone"
                value={owner?.homePhoneNumber ?? ''}
                name="ownerPrimaryPhoneNumber"
                isReadOnly={isReadOnly}
              />
              <NonEditableField
                requirement="optional"
                label="Alternate phone"
                value={owner?.workPhoneNumber ?? ''}
                name="ownerAlternatePhoneNumber"
                isReadOnly={isReadOnly}
              />
            </div>
          </div>
          <h3>Damage estimate information</h3>
          <GoASpacer vSpacing="xl" />
          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Damage estimate obtained?"
              value={damageEstimateObtained ? 'Yes' : 'No'}
              name="damageEstimateObtained"
              isReadOnly={isReadOnly}
            />

            <EditableField
              onChange={(value) => {
                setIsSaved(false);
                setDamageEstimate(value);
              }}
              setPendingChange={() => {
                setTriggerSave(true);
                dispatch(
                  pushNotification({
                    body: `You change or updates have been saved`,
                    notificationType: NotificationType.informationGeneric,
                  }),
                );
              }}
              showEditButton={
                editMode &&
                isReportClaimedAndAssignedToMe() &&
                damageEstimateObtained == false
              }
              label={
                damageEstimate && damageEstimate != '0'
                  ? 'Damage estimate'
                  : 'Damage estimate to be filled in by Law Enforcement'
              }
              value={damageEstimate}
              name="damageEstimate"
              validator={(value) => {
                return value.length > 0;
              }}
              error={
                'Enter a damage estimate amount based on damage photos provided'
              }
              isReadOnly={isReadOnly}
              leadingContent="$"
              requirement={damageEstimateObtained ? 'optional' : 'required'}
            />
          </div>
          <h3>Insurance information</h3>
          <GoASpacer vSpacing="xl" />
          <NonEditableField
            label="Insurance company"
            value={insuredBy?.companyName ?? ''}
            name="insuranceCompany"
            fullWidth={true}
            isReadOnly={isReadOnly}
          />
          <div className={styles.twoitemrow}>
            <NonEditableField
              label="Policy number"
              value={insuredBy?.insurancePolicyNumber ?? ''}
              name="policyNumber"
              isReadOnly={isReadOnly}
            />
            <EditableField
              label="Expiration Date"
              value={insuredByExpiration}
              onChange={(value) => {
                setIsSaved(false);
                setInsuredByExpiration(value);
              }}
              setPendingChange={() => {
                setTriggerSave(true);
                dispatch(
                  pushNotification({
                    body: `You change or updates have been saved`,
                    notificationType: NotificationType.informationGeneric,
                  }),
                );
              }}
              showEditButton={editMode && isReportClaimedAndAssignedToMe()}
              name="expirationNumber"
              type="date"
              validator={(value) => {
                return value.length > 0;
              }}
              error={'Enter a date'}
              isReadOnly={isReadOnly}
            />
          </div>
        </SectionComponent>
        <SectionComponent header="9. Passenger information">
          {collision &&
          collision.collisionType &&
          !isHitAndRun(collision.collisionType, stateConfig) ? (
            <div>
              <NonEditableField
                label="Number of passengers"
                value={passengers.length.toString()}
                name="numberOfPassengers"
                fullWidth={true}
                isReadOnly={isReadOnly}
              />
              <GoASpacer vSpacing="xl" />
              {getPassengerNodes()}{' '}
            </div>
          ) : (
            'Passenger information not applicable for hit and run collisions'
          )}
        </SectionComponent>
        <SectionComponent header="Additional collision details">
          {!isReadOnly ? (
            <GoAFormItem label="Collision description / statement">
              <GoATextArea
                name="additionalCollisionDetails"
                value={additionalDetails}
                width="670px"
                disabled={true}
              />
            </GoAFormItem>
          ) : additionalDetails ? (
            <div>
              <div>Collision description / statement</div>
              <div>{additionalDetails}</div>
            </div>
          ) : (
            ''
          )}
        </SectionComponent>
        <GoASpacer vSpacing="xl" />
      </div>
    );
  };

interface SectionComponentProps {
  header: React.ReactNode;
  children: React.ReactNode;
}

const SectionComponent = ({ header, children }: SectionComponentProps) => {
  return (
    <div>
      <h2 style={{ marginBottom: '40px' }}>{header}</h2>
      {children}
    </div>
  );
};

interface NonEditableFieldProps {
  label: string;
  name: string;
  value: string;
  fullWidth?: boolean;
  trailingIcon?: GoAIconType;
  visible?: boolean;
  requirement?: GoAFormItemRequirement;
  isReadOnly?: boolean;
}

const NonEditableField = ({
  label,
  name,
  value,
  fullWidth,
  trailingIcon,
  visible = true,
  requirement = undefined,
  isReadOnly = false,
}: NonEditableFieldProps) => {
  return (
    <div>
      <div style={{ display: displaySection(visible && isReadOnly) }}>
        <strong>{label}</strong>
        <GoASpacer vSpacing="3xs" />
        {value}
        <GoASpacer vSpacing="m" />
      </div>
      <div style={{ display: displaySection(visible && !isReadOnly) }}>
        <GoAFormItem mb={'l'} label={label} requirement={requirement}>
          <GoAInputText
            trailingIcon={trailingIcon}
            width={fullWidth ? '630px' : '284px'}
            name={name}
            disabled={true}
            value={value}
          ></GoAInputText>
        </GoAFormItem>
      </div>
    </div>
  );
};

interface EditableFieldProps {
  label: string;
  value: string;
  name: string;
  fullWidth?: boolean;
  showEditButton: boolean;
  onChange: (value: string) => void;
  setPendingChange: () => void;
  trailingIcon?: GoAIconType;
  visible?: boolean;
  requirement?: GoAFormItemRequirement;
  type?: GoAInputType;
  error?: string;
  validator?: (value: string) => boolean;
  isReadOnly?: boolean;
  leadingContent?: string;
}

const EditableField = ({
  label,
  name,
  value,
  fullWidth,
  showEditButton,
  onChange,
  setPendingChange,
  trailingIcon,
  visible = true,
  requirement = undefined,
  type = 'text',
  error,
  validator,
  isReadOnly = false,
  leadingContent,
}: EditableFieldProps): JSX.Element => {
  const [editMode, setEditMode] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorText, setErrorText] = useState('');

  useEffect(() => {
    if (isError && error) {
      setErrorText(error);
    }
  }, [isError]);

  return (
    <div
      style={{
        display: displaySection(visible),
      }}
    >
      <div style={{ display: displaySection(isReadOnly) }}>
        <strong>{label}</strong>
        <GoASpacer vSpacing="3xs" />
        {leadingContent}
        {value}
        <GoASpacer vSpacing="m" />
      </div>
      <div style={{ display: displaySection(!isReadOnly) }}>
        <GoAFormItem
          mb={'l'}
          label={label}
          requirement={requirement}
          error={errorText}
        >
          <span style={{ display: 'flex', gap: '10px' }}>
            {type === 'date' && !isReadOnly && !editMode ? (
              <GoAInput
                type="text"
                trailingIcon={trailingIcon}
                name={name}
                value={moment(value).format('MMMM DD, YYYY')}
                disabled={true}
                error={isError}
                width={fullWidth ? '630px' : '284px'}
                leadingContent={leadingContent}
              ></GoAInput>
            ) : (
              <GoAInput
                type={type}
                trailingIcon={trailingIcon}
                name={name}
                value={value}
                disabled={!editMode}
                error={isError}
                width={fullWidth ? '630px' : '284px'}
                leadingContent={leadingContent}
                onChange={(item, value) => {
                  onChange(value);
                  if (validator) {
                    const valid = validator(value);
                    setIsError(!valid);
                    if (valid) {
                      setErrorText('');
                    } else {
                      setErrorText(error!);
                    }
                  }
                }}
              ></GoAInput>
            )}
            <span
              style={{
                display: displaySection(!editMode && showEditButton),
              }}
            >
              <GoAButton
                onClick={() => {
                  setEditMode(true);
                  setIsError(false);
                  setErrorText('');
                }}
              >
                Edit
              </GoAButton>
            </span>
            <span style={{ display: displaySection(editMode) }}>
              <GoAButton
                onClick={() => {
                  const invalid = validator ? !validator(value) : true;
                  setIsError(invalid);

                  if (!invalid) {
                    setPendingChange();
                    setEditMode(false);
                    onChange(value);
                  }
                }}
              >
                Save
              </GoAButton>
            </span>
          </span>
          {/* <span style={{ display: 'flex', gap: '10px' }}>
            <GoAInput
              type={type}
              trailingIcon={trailingIcon}
              name={name}
              value={value}
              disabled={!editMode}
              error={isError}
              width={fullWidth ? '630px' : '284px'}
              leadingContent={leadingContent}
              onChange={(item, value) => {
                onChange(value);
                if (validator) {
                  const valid = validator(value);
                  setIsError(!valid);
                  if (valid) {
                    setErrorText('');
                  } else {
                    setErrorText(error!);
                  }
                }
              }}
            ></GoAInput>
            <span
              style={{ display: displaySection(!editMode && showEditButton) }}
            >
              <GoAButton
                onClick={() => {
                  setEditMode(true);
                  setIsError(false);
                  setErrorText('');
                }}
              >
                Edit
              </GoAButton>
            </span>
            <span style={{ display: displaySection(editMode) }}>
              <GoAButton
                onClick={() => {
                  const invalid = validator ? !validator(value) : true;
                  setIsError(invalid);

                  if (!invalid) {
                    setPendingChange();
                    setEditMode(false);
                    onChange(value);
                  }
                }}
              >
                Save
              </GoAButton>
            </span>
          </span> */}
        </GoAFormItem>
      </div>
    </div>
  );
};
